import { Injectable } from '@angular/core';
import {
  Firestore,
  Timestamp,
  doc,
  getDoc,
  setDoc,
} from '@angular/fire/firestore';
import { Observable, from, map } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  MileageEntries,
  MileageEntriesFirebase,
  MileageEntry,
  MileageEntryFirebase,
} from '../models/mileage.model';
import { toDate, toTimestamp } from 'src/app/shared/utils/firestore-timestamp-utils';

@Injectable({
  providedIn: 'root',
})
export class MileageService {
  constructor(private firestore: Firestore) {}

  getMileageData(vanId: string): Observable<MileageEntries | null> {
    const docRef = doc(this.firestore, `vans/${vanId}/mileageData`, 'data');
    return from(getDoc(docRef)).pipe(
      map((doc) => {
        if (doc.exists()) {
          const firebaseData = doc.data() as MileageEntriesFirebase;
          return this.transformFromFirebase(firebaseData);
        }
        return null;
      })
    );
  }

  upsertMileageData(vanId: string, data: MileageEntries): Observable<void> {
    const docRef = doc(this.firestore, `vans/${vanId}/mileageData`, 'data');
    const firebaseData = this.transformToFirebase(data);

    // Always use setDoc to write the entire document
    return from(setDoc(docRef, firebaseData));
  }

  private transformFromFirebase(
    firebaseData: MileageEntriesFirebase
  ): MileageEntries {
    const entries: MileageEntry[] = firebaseData.entries.map(
      (firebaseEntry: MileageEntryFirebase) => {
        const { date, ...restOfFirebaseEntry } = firebaseEntry;
        return {
          ...restOfFirebaseEntry,
          id: uuidv4(),
          date: toDate(date),
        };
      }
    );
    return { entries };
  }

  private transformToFirebase(data: MileageEntries): MileageEntriesFirebase {
    const entries: MileageEntryFirebase[] = data.entries.map((entryWithId) => {
      const { id, date, ...restOfEntry } = entryWithId;
      return {
        ...restOfEntry,
        date: toTimestamp(date) as Timestamp,
      };
    });
    return { entries };
  }
}
